import { useEffect, useState } from "react";
import api from "../../services/api";

const SECTIONS = ["", "OPD", "Accounts", "MRD", "Pharmacy", "Reception", "Lab"];
const ROLES    = ["ADMIN", "CONTENT", "STAFF"];
const BLANK    = { name: "", email: "", password: "", role: "ADMIN", section: "" };

/* ─────────────────────────────────────────────────────
   ALL helper components are defined OUTSIDE the parent.
   If they were inside, React would recreate the component
   type on every render → unmount + remount → focus lost.
───────────────────────────────────────────────────────── */

function Th({ children }) {
  return (
    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

function ValidatedField({ label, value, onChange, type, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type || "text"}
        className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
          error ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
        }`}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete="off"
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function PasswordField({ label, value, onChange, error }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          className={`w-full border rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 transition ${
            error ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
          }`}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setShow(p => !p)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
          tabIndex={-1}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? "🙈" : "👁️"}
        </button>
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ saving, onCancel, onSave, label }) {
  return (
    <div className="flex gap-3 pt-2">
      <button
        type="button"
        onClick={onCancel}
        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition"
      >
        {saving ? "Saving…" : label}
      </button>
    </div>
  );
}

/* ── Validation ── */
function validateForm(form, isEdit) {
  const errors = {};
  if (!form.name.trim()) {
    errors.name = "Name is required";
  } else if (!/^[a-zA-Z\s'.\\-]+$/.test(form.name.trim())) {
    errors.name = "Name should contain only letters and spaces";
  }
  if (!form.email.trim()) {
    errors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(form.email)) {
    errors.email = "Enter a valid email address";
  }
  if (!isEdit && !form.password) {
    errors.password = "Password is required for new users";
  }
  if (form.password && form.password.length < 6) {
    errors.password = "Password must be at least 6 characters";
  }
  return errors;
}

/* ── Main component ── */
export default function AdminUsers() {
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(BLANK);
  const [errors,   setErrors]   = useState({});
  const [saving,   setSaving]   = useState(false);
  const [apiError, setApiError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setErrors({});
    setApiError("");
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, section: u.section || "" });
    setErrors({});
    setApiError("");
    setShowForm(true);
  };

  const save = async () => {
    const errs = validateForm(form, !!editing);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    setApiError("");
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;

      if (editing) {
        await api.put(`/admin/users/${editing._id}`, payload);
      } else {
        await api.post("/admin/create", payload);
      }
      setShowForm(false);
      load();
    } catch (e) {
      setApiError(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this admin user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      if (editing?._id === id) { setEditing(null); setShowForm(false); }
      load();
    }
    catch (e) { alert(e.response?.data?.error || "Delete failed"); }
  };

  const setField = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: "" }));
  };

  return (
    <div className="space-y-6 max-w-5xl">

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">👥 Admin Users</h1>
          <p className="text-sm text-gray-500">Create and manage admin staff accounts for different departments</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition"
        >
          + Create Admin
        </button>
      </div>

      {/* Role explanation */}
      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        {[
          { role: "MASTER_ADMIN", color: "purple", desc: "Full system control. Can create/delete all users." },
          { role: "ADMIN",        color: "blue",   desc: "Operational access. Can manage assigned section."  },
          { role: "CONTENT",      color: "teal",   desc: "CMS only — can edit website pages."               },
        ].map(r => (
          <div key={r.role} className={`bg-${r.color}-50 border border-${r.color}-100 rounded-xl p-3`}>
            <p className={`font-semibold text-${r.color}-700 mb-1`}>{r.role}</p>
            <p className="text-gray-500">{r.desc}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <p className="text-center py-12 text-gray-400 text-sm">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-center py-12 text-gray-400 text-sm">No admin users yet. Create one above.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Section</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => (
                <tr key={u._id} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-3.5 font-medium text-gray-800">{u.name}</td>
                  <td className="px-5 py-3.5 text-gray-500">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      u.role === "MASTER_ADMIN" ? "bg-purple-100 text-purple-700" :
                      u.role === "ADMIN"        ? "bg-blue-100 text-blue-700"     :
                      u.role === "CONTENT"      ? "bg-teal-100 text-teal-700"     :
                                                  "bg-gray-100 text-gray-600"
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-5 py-3.5 text-gray-400">{u.section || "—"}</td>
                  <td className="px-5 py-3.5">
                    {u.role !== "MASTER_ADMIN" ? (
                      <div className="flex gap-3">
                        <button type="button" onClick={() => openEdit(u)} className="text-blue-500 hover:text-blue-700 text-xs font-medium">Edit</button>
                        <button type="button" onClick={() => del(u._id)}  className="text-red-400 hover:text-red-600 text-xs font-medium">Delete</button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">Protected</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <Modal title={editing ? "Edit Admin User" : "Create Admin User"} onClose={() => setShowForm(false)}>
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-2 text-sm">
              {apiError}
            </div>
          )}

          <ValidatedField
            label="Full Name *"
            value={form.name}
            onChange={v => setField("name", v)}
            error={errors.name}
          />
          <ValidatedField
            label="Email *"
            value={form.email}
            onChange={v => setField("email", v)}
            type="email"
            error={errors.email}
          />
          <PasswordField
            label={editing ? "New Password (leave blank to keep)" : "Password *"}
            value={form.password}
            onChange={v => setField("password", v)}
            error={errors.password}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
              value={form.role}
              onChange={e => setField("role", e.target.value)}
            >
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department / Section</label>
            <select
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
              value={form.section}
              onChange={e => setField("section", e.target.value)}
            >
              <option value="">— All / General —</option>
              {SECTIONS.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">Assign this admin to a specific department (optional).</p>
          </div>

          <ModalActions
            saving={saving}
            onCancel={() => setShowForm(false)}
            onSave={save}
            label={editing ? "Save Changes" : "Create Admin"}
          />
        </Modal>
      )}

    </div>
  );
}